import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Pencil, Trash2, Users as UsersIcon, Shield, Settings, Clock, Wifi, Warehouse, Gauge, Wallet, HeartPulse, HandHeart, KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PermissionsDialog } from '../components/users/PermissionsDialog';
import { fmtDateTime } from '../lib/datetime';
import { PERMISSION_GROUPS, GROUP_COLORS, PERMISSION_MODULES } from '../lib/permissionsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Users() {
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [cashBanks, setCashBanks] = useState([]);
  const [internalAreas, setInternalAreas] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [cashBankDialogOpen, setCashBankDialogOpen] = useState(false);
  const [areasDialogOpen, setAreasDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ role: '', is_active: true });
  const [editingPermissions, setEditingPermissions] = useState({});
  const [copyFromId, setCopyFromId] = useState('');
  const [openPermGroups, setOpenPermGroups] = useState({});
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [editingWarehouses, setEditingWarehouses] = useState([]);
  const [editingCashBanks, setEditingCashBanks] = useState([]);
  const [editingAreas, setEditingAreas] = useState([]);
  const [editingRemAreas, setEditingRemAreas] = useState([]);
  const [limitsDialogOpen, setLimitsDialogOpen] = useState(false);
  const [pwdDialog, setPwdDialog] = useState({ open: false, user: null, password: '', confirm: '', saving: false, showPwd: false });
  const [editingLimits, setEditingLimits] = useState({
    max_exit_quantity: '',
    max_np_amount: '',
    requires_approval_above: ''
  });
  
  useEffect(() => {
    fetchUsers();
    fetchWarehouses();
    fetchCashBanks();
    fetchInternalAreas();
    fetchSuperAdmins();
  }, []);

  const fetchSuperAdmins = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/users/super-admins`, { withCredentials: true });
      setSuperAdmins((r.data?.emails || []).map(e => e.toLowerCase()));
    } catch (e) {
      // No critico: si no llega, el frontend simplemente no muestra el candado.
      console.warn('fetchSuperAdmins fallo (no critico):', e?.response?.status || e?.message);
    }
  };

  const fetchCashBanks = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/treasury/cash-banks`, { withCredentials: true });
      setCashBanks(r.data || []);
    } catch (e) {
      // Si el admin no tiene Tesorería habilitada, devuelve 403. No crítico.
      console.warn('fetchCashBanks fallo (no critico):', e?.response?.status || e?.message);
    }
  };

  const fetchInternalAreas = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/internal-areas`, { withCredentials: true });
      setInternalAreas(r.data || []);
    } catch (e) {
      // No critico
      console.warn('fetchInternalAreas fallo (no critico):', e?.response?.status || e?.message);
    }
  };

  const isProtected = (user) => user?.email && superAdmins.includes(user.email.toLowerCase());
  
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/warehouses`, { withCredentials: true });
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error al cargar depósitos');
    }
  };
  
  const fetchDefaultPermissions = async (role) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/permissions/default/${role}`, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('Error al cargar permisos por defecto:', error);
      return {};
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${BACKEND_URL}/api/users/${editingUser.user_id}`, formData, { withCredentials: true });
      toast.success('Usuario actualizado');
      setEditDialogOpen(false);
      setEditingUser(null);
      setFormData({ role: '', is_active: true });
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar usuario');
    }
  };
  
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ 
      role: user.role,
      is_active: user.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const openSetPassword = (user) => {
    setPwdDialog({ open: true, user, password: '', confirm: '', saving: false, showPwd: false });
  };

  const submitSetPassword = async () => {
    const { user, password, confirm } = pwdDialog;
    if (!password || password.length < 8) { toast.error('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (password.toLowerCase() === password) { toast.error('La contraseña debe tener al menos una mayúscula.'); return; }
    if (!/\d/.test(password)) { toast.error('La contraseña debe tener al menos un dígito.'); return; }
    if (password !== confirm) { toast.error('Las contraseñas no coinciden.'); return; }
    setPwdDialog(p => ({ ...p, saving: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/users/${user.user_id}/set-password`,
        { new_password: password },
        { withCredentials: true }
      );
      toast.success(`Contraseña local definida para ${user.name}. Ya puede ingresar con email + contraseña.`);
      setPwdDialog({ open: false, user: null, password: '', confirm: '', saving: false, showPwd: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar la contraseña');
      setPwdDialog(p => ({ ...p, saving: false }));
    }
  };
  
  const handleEditPermissions = async (user) => {
    setEditingUser(user);
    
    // Cargar permisos por defecto del rol
    const defaults = await fetchDefaultPermissions(user.role);
    setDefaultPermissions(defaults);
    
    // Usar permisos personalizados si existen, sino los por defecto
    const currentPermissions = user.permissions || defaults;
    setEditingPermissions(JSON.parse(JSON.stringify(currentPermissions)));
    setCopyFromId('');
    setPermissionsDialogOpen(true);
  };
  
  const handleSavePermissions = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.user_id}`, 
        { permissions: editingPermissions }, 
        { withCredentials: true }
      );
      toast.success('Permisos actualizados');
      setPermissionsDialogOpen(false);
      setEditingUser(null);
      setEditingPermissions({});
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar permisos');
    }
  };
  
  const handleResetToDefault = async () => {
    const defaults = await fetchDefaultPermissions(editingUser.role);
    setEditingPermissions(JSON.parse(JSON.stringify(defaults)));
    setCopyFromId('');
    toast.info('Permisos restablecidos a valores por defecto del rol');
  };

  const handleCopyFromUser = async (sourceUserId) => {
    setCopyFromId(sourceUserId);
    if (!sourceUserId) return;
    const src = users.find(u => u.user_id === sourceUserId);
    if (!src) return;
    let perms = src.permissions;
    if (!perms || Object.keys(perms).length === 0) {
      perms = await fetchDefaultPermissions(src.role);
    }
    setEditingPermissions(JSON.parse(JSON.stringify(perms)));
    toast.success(`Permisos copiados de ${src.name}. Revisá y guardá para aplicar.`);
  };
  
  const handlePermissionChange = (moduleKey, permKey, value) => {
    setEditingPermissions(prev => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [permKey]: value },
    }));
  };

  // v3.30.13 · ON/OFF total por grupo de permisos. Al togglear se aplica el
  // nuevo valor a TODOS los permisos de TODOS los módulos del grupo.
  const groupPermissionState = (group) => {
    const moduleByKey = Object.fromEntries(PERMISSION_MODULES.map(m => [m.key, m]));
    let total = 0, on = 0;
    for (const k of group.keys) {
      const m = moduleByKey[k];
      if (!m) continue;
      for (const p of m.permissions) {
        total += 1;
        if (editingPermissions[m.key]?.[p.key]) on += 1;
      }
    }
    return { total, on, all: total > 0 && on === total };
  };
  const handleGroupToggle = (group, value) => {
    const moduleByKey = Object.fromEntries(PERMISSION_MODULES.map(m => [m.key, m]));
    setEditingPermissions(prev => {
      const next = { ...prev };
      for (const k of group.keys) {
        const m = moduleByKey[k];
        if (!m) continue;
        const modPerms = { ...(next[m.key] || {}) };
        for (const p of m.permissions) modPerms[p.key] = !!value;
        next[m.key] = modPerms;
      }
      return next;
    });
  };
  
  const handleEditWarehouses = (user) => {
    setEditingUser(user);
    // null/vacío en el usuario = "todos los depósitos" → mostrar todos los switches encendidos
    const allIds = warehouses.map(w => w.warehouse_id);
    setEditingWarehouses(
      user.allowed_warehouses && user.allowed_warehouses.length > 0 ? user.allowed_warehouses : allIds
    );
    setWarehouseDialogOpen(true);
  };
  
  const handleWarehouseToggle = (warehouseId) => {
    setEditingWarehouses(prev => {
      if (prev.includes(warehouseId)) {
        return prev.filter(id => id !== warehouseId);
      }
      return [...prev, warehouseId];
    });
  };
  
  const handleSaveWarehouses = async () => {
    try {
      const allowedWarehouses = editingWarehouses.length === 0 || editingWarehouses.length === warehouses.length
        ? null  // null = todos los depósitos
        : editingWarehouses;
      
      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.user_id}`,
        { allowed_warehouses: allowedWarehouses },
        { withCredentials: true }
      );
      toast.success('Depósitos actualizados');
      setWarehouseDialogOpen(false);
      setEditingUser(null);
      setEditingWarehouses([]);
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar depósitos');
    }
  };
  
  const handleSelectAllWarehouses = () => {
    if (editingWarehouses.length === warehouses.length) {
      setEditingWarehouses([]);
    } else {
      setEditingWarehouses(warehouses.map(w => w.warehouse_id));
    }
  };

  // ===== Cajas/Bancos =====
  const handleEditCashBanks = (user) => {
    setEditingUser(user);
    setEditingCashBanks(user.allowed_cash_banks || []);
    setCashBankDialogOpen(true);
  };
  const handleCashBankToggle = (cashBankId) => {
    setEditingCashBanks(prev => prev.includes(cashBankId)
      ? prev.filter(id => id !== cashBankId)
      : [...prev, cashBankId]);
  };
  const handleSelectAllCashBanks = () => {
    if (editingCashBanks.length === cashBanks.length) {
      setEditingCashBanks([]);
    } else {
      setEditingCashBanks(cashBanks.map(cb => cb.cash_bank_id));
    }
  };
  const handleSaveCashBanks = async () => {
    try {
      const allowed = editingCashBanks.length === 0 || editingCashBanks.length === cashBanks.length
        ? null
        : editingCashBanks;
      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.user_id}`,
        { allowed_cash_banks: allowed },
        { withCredentials: true }
      );
      toast.success('Cajas/Bancos actualizados');
      setCashBankDialogOpen(false);
      setEditingUser(null);
      setEditingCashBanks([]);
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar cajas/bancos');
    }
  };

  // ===== Áreas Internas =====
  const handleEditAreas = async (user) => {
    setEditingUser(user);
    setEditingRemAreas(user.rem_target_area_ids || []);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/users/${user.user_id}/areas`, { withCredentials: true });
      setEditingAreas(r.data?.area_ids || []);
    } catch (e) {
      setEditingAreas([]);
    }
    setAreasDialogOpen(true);
  };
  const handleAreaToggle = (areaId) => {
    setEditingAreas(prev => prev.includes(areaId)
      ? prev.filter(id => id !== areaId)
      : [...prev, areaId]);
  };
  const handleRemAreaToggle = (areaId) => {
    setEditingRemAreas(prev => prev.includes(areaId)
      ? prev.filter(id => id !== areaId)
      : [...prev, areaId]);
  };
  const handleSelectAllAreas = () => {
    if (editingAreas.length === internalAreas.length) {
      setEditingAreas([]);
    } else {
      setEditingAreas(internalAreas.map(a => a.area_id));
    }
  };
  const handleSaveAreas = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.user_id}/areas`,
        { area_ids: editingAreas },
        { withCredentials: true }
      );
      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.user_id}`,
        { rem_target_area_ids: editingRemAreas },
        { withCredentials: true }
      );
      toast.success('Áreas actualizadas');
      setAreasDialogOpen(false);
      setEditingUser(null);
      setEditingAreas([]);
      setEditingRemAreas([]);
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar áreas');
    }
  };
  
  const handleEditLimits = (user) => {
    setEditingUser(user);
    const limits = user.operation_limits || {};
    setEditingLimits({
      max_exit_quantity: limits.max_exit_quantity ?? '',
      max_np_amount: limits.max_np_amount ?? '',
      requires_approval_above: limits.requires_approval_above ?? ''
    });
    setLimitsDialogOpen(true);
  };
  
  const handleSaveLimits = async () => {
    try {
      const limits = {};
      if (editingLimits.max_exit_quantity !== '') {
        limits.max_exit_quantity = parseInt(editingLimits.max_exit_quantity);
      }
      if (editingLimits.max_np_amount !== '') {
        limits.max_np_amount = parseFloat(editingLimits.max_np_amount);
      }
      if (editingLimits.requires_approval_above !== '') {
        limits.requires_approval_above = parseFloat(editingLimits.requires_approval_above);
      }
      
      const operationLimits = Object.keys(limits).length > 0 ? limits : null;
      
      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.user_id}`,
        { operation_limits: operationLimits },
        { withCredentials: true }
      );
      toast.success('Límites actualizados');
      setLimitsDialogOpen(false);
      setEditingUser(null);
      setEditingLimits({ max_exit_quantity: '', max_np_amount: '', requires_approval_above: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar límites');
    }
  };
  
  const handleClearLimits = () => {
    setEditingLimits({ max_exit_quantity: '', max_np_amount: '', requires_approval_above: '' });
  };
  
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${confirmDelete.user_id}`, { withCredentials: true });
      toast.success('Usuario eliminado');
      setConfirmDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeleting(false);
    }
  };
  
  const getRoleBadgeVariant = (role) => {
    if (role === 'admin') return 'default';
    if (role === 'supervisor') return 'secondary';
    return 'outline';
  };
  
  const getRoleLabel = (role) => {
    if (role === 'admin') return 'SUPER ADMIN';
    if (role === 'supervisor') return 'SUPERVISOR';
    return 'OPERADOR';
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return fmtDateTime(dateStr);
  };
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Usuarios</h1>
          <p className="text-muted-foreground">Gestiona usuarios y permisos personalizados</p>
        </div>
      </div>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Último Acceso</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id} data-testid="user-row" className={user.is_active === false ? 'opacity-50' : ''}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar>
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback>{user.name?.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <span className="font-medium block">{user.name}</span>
                        {user.permissions && (
                          <span className="text-xs text-blue-600 block">Permisos personalizados</span>
                        )}
                        {user.allowed_warehouses && user.allowed_warehouses.length > 0 && (
                          <span className="text-xs text-indigo-600 block">
                            {user.allowed_warehouses.length} depósito(s)
                          </span>
                        )}
                        {user.operation_limits && Object.keys(user.operation_limits).length > 0 && (
                          <span className="text-xs text-amber-600 block">
                            Con límites
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={getRoleBadgeVariant(user.role)} className="uppercase">
                        <Shield className="w-3 h-3 mr-1" />
                        {getRoleLabel(user.role)}
                      </Badge>
                      {isProtected(user) && (
                        <span title="Cuenta protegida (SUPER_ADMIN_EMAILS)">🔒</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.is_active !== false ? (
                      <Badge className="bg-green-100 text-green-800">Activo</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800">Inactivo</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {user.last_login ? (
                        <>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatDate(user.last_login)}
                          </div>
                          {user.last_ip && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                              <Wifi className="w-3 h-3" />
                              {user.last_ip}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-1">
                      {user.role !== 'admin' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditPermissions(user)} 
                            title="Configurar permisos"
                            data-testid="edit-permissions-button"
                          >
                            <Settings className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditWarehouses(user)} 
                            title="Configurar depósitos permitidos"
                            data-testid="edit-warehouses-button"
                          >
                            <Warehouse className="w-4 h-4 text-indigo-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditCashBanks(user)} 
                            title="Configurar cajas/bancos permitidos (Tesorería)"
                            data-testid="edit-cashbanks-button"
                          >
                            <Wallet className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditAreas(user)} 
                            title="Configurar áreas internas asignadas (Enfermería / Internas)"
                            data-testid="edit-areas-button"
                          >
                            <HeartPulse className="w-4 h-4 text-rose-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditLimits(user)} 
                            title="Configurar límites de operación"
                            data-testid="edit-limits-button"
                          >
                            <Gauge className="w-4 h-4 text-amber-600" />
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openSetPassword(user)} 
                        data-testid="set-password-button"
                        title="Setear contraseña local (para login por email/contraseña)"
                      >
                        <KeyRound className="w-4 h-4 text-violet-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleEdit(user)} 
                        data-testid="edit-user-button"
                        title="Editar usuario"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!isProtected(user) && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => setConfirmDelete(user)} 
                          data-testid="delete-user-button"
                          title="Eliminar usuario"
                        >
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {users.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <UsersIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay usuarios registrados.</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog de edición básica */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingUser(null); setFormData({ role: '', is_active: true }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>Modifica el rol y estado del usuario</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Usuario</Label>
              <p className="text-sm font-medium mt-1">{editingUser?.name}</p>
              <p className="text-xs text-muted-foreground">{editingUser?.email}</p>
            </div>
            {isProtected(editingUser) && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                🔒 Cuenta protegida (SUPER_ADMIN_EMAILS). El rol y el estado no se pueden modificar.
              </div>
            )}
            <div>
              <Label htmlFor="role">Rol (Plantilla base) *</Label>
              <select
                id="role"
                data-testid="user-role-select"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                required
                disabled={isProtected(editingUser)}
              >
                <option value="operator">Operador</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Super Admin</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                El rol define los permisos base. Puedes personalizarlos después.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_active">Estado del usuario</Label>
                <p className="text-xs text-muted-foreground">Los usuarios inactivos no pueden acceder al sistema</p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                disabled={isProtected(editingUser)}
              />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="user-submit-button">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de permisos (v3.31.6 · extraído a components/users/PermissionsDialog.jsx) */}
      <PermissionsDialog
        open={permissionsDialogOpen}
        onOpenChange={(open) => { setPermissionsDialogOpen(open); if (!open) { setEditingUser(null); setEditingPermissions({}); setCopyFromId(''); } }}
        editingUser={editingUser}
        editingPermissions={editingPermissions}
        users={users}
        copyFromId={copyFromId}
        openPermGroups={openPermGroups}
        setOpenPermGroups={setOpenPermGroups}
        getRoleLabel={getRoleLabel}
        onReset={handleResetToDefault}
        onCopyFrom={handleCopyFromUser}
        onSave={handleSavePermissions}
        onCancel={() => setPermissionsDialogOpen(false)}
        onPermissionChange={handlePermissionChange}
        onGroupToggle={handleGroupToggle}
        groupPermissionState={groupPermissionState}
      />
      
      {/* Warehouses Dialog */}
      <Dialog open={warehouseDialogOpen} onOpenChange={(open) => { setWarehouseDialogOpen(open); if (!open) { setEditingUser(null); setEditingWarehouses([]); } }}>
        <DialogContent className="max-w-md" data-testid="warehouses-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-indigo-600" />
              Depósitos Permitidos
            </DialogTitle>
            <DialogDescription>
              {editingUser?.name} ({editingUser?.role?.toUpperCase()})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm text-amber-700 dark:text-amber-300">
              Selecciona los depósitos a los que este usuario tendrá acceso para crear movimientos y documentos. 
              Si no seleccionas ninguno, tendrá acceso a todos.
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {editingWarehouses.length === 0 || editingWarehouses.length === warehouses.length
                  ? 'Todos los depósitos'
                  : `${editingWarehouses.length} de ${warehouses.length} depósitos`}
              </span>
              <Button variant="outline" size="sm" onClick={handleSelectAllWarehouses}>
                {editingWarehouses.length === warehouses.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </Button>
            </div>
            
            <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
              {warehouses.map((warehouse) => (
                <div key={warehouse.warehouse_id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Warehouse className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="font-medium">{warehouse.name}</span>
                      {warehouse.location && (
                        <p className="text-xs text-muted-foreground">{warehouse.location}</p>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={editingWarehouses.includes(warehouse.warehouse_id)}
                    onCheckedChange={() => handleWarehouseToggle(warehouse.warehouse_id)}
                    data-testid={`warehouse-switch-${warehouse.warehouse_id}`}
                  />
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setWarehouseDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveWarehouses}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cash/Banks Dialog */}
      <Dialog open={cashBankDialogOpen} onOpenChange={(open) => { setCashBankDialogOpen(open); if (!open) { setEditingUser(null); setEditingCashBanks([]); } }}>
        <DialogContent className="max-w-md" data-testid="cashbanks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" />
              Cajas / Bancos Permitidos
            </DialogTitle>
            <DialogDescription>
              {editingUser?.name} ({editingUser?.role?.toUpperCase()})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm text-amber-700 dark:text-amber-300">
              Restringe el acceso del usuario a cajas/bancos específicos en el módulo de Tesorería.
              Si no seleccionas ninguno, tendrá acceso a todos.
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {editingCashBanks.length === 0 || editingCashBanks.length === cashBanks.length
                  ? 'Todas las cajas/bancos'
                  : `${editingCashBanks.length} de ${cashBanks.length}`}
              </span>
              <Button variant="outline" size="sm" onClick={handleSelectAllCashBanks}>
                {editingCashBanks.length === cashBanks.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </Button>
            </div>
            
            <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
              {cashBanks.length === 0 && (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  No hay cajas/bancos configurados todavía. Cargá los maestros en Tesorería.
                </div>
              )}
              {cashBanks.map((cb) => (
                <div key={cb.cash_bank_id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="font-medium">{cb.name}</span>
                      <p className="text-xs text-muted-foreground">
                        {cb.entity_type === 'cash' ? 'Caja' : 'Banco'} · {cb.currency}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={editingCashBanks.includes(cb.cash_bank_id)}
                    onCheckedChange={() => handleCashBankToggle(cb.cash_bank_id)}
                    data-testid={`cashbank-switch-${cb.cash_bank_id}`}
                  />
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCashBankDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCashBanks} data-testid="cashbanks-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Internal Areas Dialog */}
      <Dialog open={areasDialogOpen} onOpenChange={(open) => { setAreasDialogOpen(open); if (!open) { setEditingUser(null); setEditingAreas([]); setEditingRemAreas([]); } }}>
        <DialogContent className="max-w-md" data-testid="areas-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-rose-600" />
              Áreas Internas Asignadas
            </DialogTitle>
            <DialogDescription>
              {editingUser?.name} ({editingUser?.role?.toUpperCase()})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm text-amber-700 dark:text-amber-300">
              Asignar áreas internas (ej. Office Planta Alta) habilita al usuario a crear y gestionar pedidos internos
              dentro de esas áreas. Sin áreas asignadas, el usuario no podrá operar Pedidos Internos.
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {editingAreas.length === 0
                  ? 'Sin áreas asignadas'
                  : editingAreas.length === internalAreas.length
                    ? 'Todas las áreas'
                    : `${editingAreas.length} de ${internalAreas.length}`}
              </span>
              <Button variant="outline" size="sm" onClick={handleSelectAllAreas}>
                {editingAreas.length === internalAreas.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
              </Button>
            </div>
            
            <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
              {internalAreas.length === 0 && (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  No hay áreas internas creadas todavía.
                </div>
              )}
              {internalAreas.map((area) => (
                <div key={area.area_id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <HeartPulse className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="font-medium">{area.name}</span>
                      {area.description && (
                        <p className="text-xs text-muted-foreground">{area.description}</p>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={editingAreas.includes(area.area_id)}
                    onCheckedChange={() => handleAreaToggle(area.area_id)}
                    data-testid={`area-switch-${area.area_id}`}
                  />
                </div>
              ))}
            </div>

            {/* Áreas destino de REM (sin ser miembro) */}
            <div className="p-3 bg-rose-50 dark:bg-rose-950/40 rounded-lg text-sm text-rose-700 dark:text-rose-300">
              <strong>Áreas destino de REM (Ingreso de familiar).</strong> Habilitá acá los subdepósitos
              de medicamentos (ej. VERDE / VIOLETA) donde este usuario puede <strong>ingresar REM</strong> aunque
              <strong> no sea miembro</strong> del área. No le da acceso a pedidos internos ni al stock de esas áreas.
            </div>
            <div className="border rounded-lg divide-y max-h-[240px] overflow-y-auto" data-testid="rem-areas-list">
              {internalAreas.filter(a => a.allow_rem !== false).length === 0 && (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  No hay áreas autorizadas a recibir REM.
                </div>
              )}
              {internalAreas.filter(a => a.allow_rem !== false).map((area) => (
                <div key={area.area_id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <HandHeart className="w-4 h-4 text-rose-500" />
                    <div>
                      <span className="font-medium">{area.name}</span>
                      {editingAreas.includes(area.area_id) && (
                        <p className="text-xs text-muted-foreground">Ya es miembro (puede REM por membresía)</p>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={editingRemAreas.includes(area.area_id)}
                    onCheckedChange={() => handleRemAreaToggle(area.area_id)}
                    data-testid={`rem-area-switch-${area.area_id}`}
                  />
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAreasDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveAreas} data-testid="areas-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Limits Dialog */}
      <Dialog open={limitsDialogOpen} onOpenChange={(open) => { setLimitsDialogOpen(open); if (!open) { setEditingUser(null); setEditingLimits({ max_exit_quantity: '', max_np_amount: '', requires_approval_above: '' }); } }}>
        <DialogContent className="max-w-md" data-testid="limits-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-amber-600" />
              Límites de Operación
            </DialogTitle>
            <DialogDescription>
              {editingUser?.name} ({editingUser?.role?.toUpperCase()})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm text-amber-700 dark:text-amber-300">
              Define límites máximos para las operaciones de este usuario. 
              Deja vacío para no aplicar límites.
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="max_exit_quantity" className="text-sm font-medium">
                  Máximo de unidades por salida
                </Label>
                <Input
                  id="max_exit_quantity"
                  type="number"
                  min="0"
                  placeholder="Sin límite"
                  value={editingLimits.max_exit_quantity}
                  onChange={(e) => setEditingLimits(prev => ({ ...prev, max_exit_quantity: e.target.value }))}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  El usuario no podrá crear salidas de stock mayores a esta cantidad
                </p>
              </div>
              
              <div>
                <Label htmlFor="max_np_amount" className="text-sm font-medium">
                  Monto máximo por Nota de Pedido ($)
                </Label>
                <Input
                  id="max_np_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Sin límite"
                  value={editingLimits.max_np_amount}
                  onChange={(e) => setEditingLimits(prev => ({ ...prev, max_np_amount: e.target.value }))}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  El usuario no podrá crear NP con monto total superior
                </p>
              </div>
              
              <div>
                <Label htmlFor="requires_approval_above" className="text-sm font-medium">
                  Requiere aprobación especial sobre ($)
                </Label>
                <Input
                  id="requires_approval_above"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Sin límite"
                  value={editingLimits.requires_approval_above}
                  onChange={(e) => setEditingLimits(prev => ({ ...prev, requires_approval_above: e.target.value }))}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  NP sobre este monto se marcarán para aprobación adicional
                </p>
              </div>
            </div>
            
            <Button variant="outline" size="sm" onClick={handleClearLimits} className="w-full">
              Quitar todos los límites
            </Button>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setLimitsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveLimits}>Guardar Límites</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Eliminar usuario"
        description={<>¿Estás seguro de eliminar al usuario <strong>{confirmDelete?.name}</strong> ({confirmDelete?.email})? Esta acción no se puede deshacer.</>}
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-user"
      />

      {/* Dialog: Setear contraseña local */}
      <Dialog open={pwdDialog.open} onOpenChange={(o) => !o && setPwdDialog({ open: false, user: null, password: '', confirm: '', saving: false, showPwd: false })}>
        <DialogContent data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-violet-600" />
              Setear contraseña local
            </DialogTitle>
            <DialogDescription>
              Definí una contraseña para que <strong>{pwdDialog.user?.name}</strong> ({pwdDialog.user?.email}) pueda
              ingresar también con email + contraseña, además del login con Google.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-3 text-xs text-blue-800 dark:text-blue-200">
              💡 Recordá compartir la contraseña con el usuario por un canal seguro. Una vez que ingrese, puede cambiarla desde su Perfil.
              <br/>Requisitos: mínimo 8 caracteres, una mayúscula y un dígito.
            </div>
            <div>
              <Label htmlFor="new-pwd" className="text-xs">Nueva contraseña</Label>
              <div className="relative">
                <Input
                  id="new-pwd"
                  data-testid="set-password-input"
                  type={pwdDialog.showPwd ? 'text' : 'password'}
                  value={pwdDialog.password}
                  onChange={(e) => setPwdDialog(p => ({ ...p, password: e.target.value }))}
                  autoComplete="new-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="set-password-toggle-visibility"
                  onClick={() => setPwdDialog(p => ({ ...p, showPwd: !p.showPwd }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {pwdDialog.showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="new-pwd-confirm" className="text-xs">Confirmar contraseña</Label>
              <Input
                id="new-pwd-confirm"
                data-testid="set-password-confirm-input"
                type={pwdDialog.showPwd ? 'text' : 'password'}
                value={pwdDialog.confirm}
                onChange={(e) => setPwdDialog(p => ({ ...p, confirm: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialog({ open: false, user: null, password: '', confirm: '', saving: false, showPwd: false })}>Cancelar</Button>
            <Button
              onClick={submitSetPassword}
              disabled={pwdDialog.saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="set-password-submit"
            >
              {pwdDialog.saving ? 'Guardando...' : 'Guardar contraseña'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Users;
